import type { Plan } from './types';
import { isUnlimitedPages } from './planChange';

/** Per-site snapshot after loading pages and blog posts from the API. */
export interface SiteContentSnapshot {
  siteId: string;
  siteName: string;
  pageCount: number;
  blogPostCount: number;
  pages: { id: string; title: string }[];
  posts: { id: string; title: string }[];
}

export function targetPlanLimits(targetPlan: Plan): { maxSites: number; maxPagesPerSite: number } {
  return {
    maxSites: Math.max(1, Number(targetPlan.maxSites) || 1),
    maxPagesPerSite: targetPlan.maxPagesPerSite,
  };
}

export function combinedCount(s: Pick<SiteContentSnapshot, 'pageCount' | 'blogPostCount'>): number {
  return s.pageCount + s.blogPostCount;
}

export function needsSiteDeletionStep(siteCount: number, maxSites: number): boolean {
  return siteCount > maxSites;
}

/** Minimum number of sites the user must mark for deletion (not "can delete more"). */
export function minSitesUserMustDelete(siteCount: number, maxSites: number): number {
  return Math.max(0, siteCount - maxSites);
}

export function isValidSiteDeletionSelection(
  siteCount: number,
  maxSites: number,
  selectedDeleteCount: number
): boolean {
  const minD = minSitesUserMustDelete(siteCount, maxSites);
  return selectedDeleteCount >= minD && siteCount - selectedDeleteCount <= maxSites;
}

export function remainingSiteIds(allIds: string[], siteIdsToDelete: Set<string>): string[] {
  return allIds.filter(id => !siteIdsToDelete.has(id));
}

export function snapshotsForRemainingSites(
  snapshots: SiteContentSnapshot[],
  siteIdsToDelete: Set<string>
): SiteContentSnapshot[] {
  return snapshots.filter(s => !siteIdsToDelete.has(s.siteId));
}

export function excessCombinedForSite(s: SiteContentSnapshot, maxPagesPerSite: number): number {
  if (isUnlimitedPages(maxPagesPerSite)) return 0;
  return Math.max(0, combinedCount(s) - maxPagesPerSite);
}

export function needsContentDeletionStep(
  remainingSites: SiteContentSnapshot[],
  maxPagesPerSite: number
): boolean {
  if (isUnlimitedPages(maxPagesPerSite)) return false;
  return remainingSites.some(s => combinedCount(s) > maxPagesPerSite);
}

/**
 * Whether the user must use the resource wizard before changing to the target plan.
 * Call with live snapshots (all sites).
 */
export function needsDowngradeResourceWizard(
  snapshots: SiteContentSnapshot[],
  maxSites: number,
  maxPagesPerSite: number
): boolean {
  if (snapshots.length > maxSites) return true;
  if (isUnlimitedPages(maxPagesPerSite)) return false;
  return snapshots.some(s => combinedCount(s) > maxPagesPerSite);
}

/**
 * Minimum combined (page + post) rows to remove for this site to be at or under the cap.
 */
export function minContentRowsToDelete(s: SiteContentSnapshot, maxPagesPerSite: number): number {
  return excessCombinedForSite(s, maxPagesPerSite);
}

export function isContentSelectionValidForSite(
  site: SiteContentSnapshot,
  maxPagesPerSite: number,
  pageIdsToDelete: Set<string>,
  postIdsToDelete: Set<string>
): boolean {
  if (isUnlimitedPages(maxPagesPerSite)) return true;
  const remPages = site.pages.filter(p => !pageIdsToDelete.has(p.id)).length;
  const remPosts = site.posts.filter(p => !postIdsToDelete.has(p.id)).length;
  return remPages + remPosts <= maxPagesPerSite;
}

export function isAllContentSelectionValid(
  sites: SiteContentSnapshot[],
  maxPagesPerSite: number,
  getPageDeletes: (siteId: string) => Set<string>,
  getPostDeletes: (siteId: string) => Set<string>
): boolean {
  return sites.every(s =>
    isContentSelectionValidForSite(
      s,
      maxPagesPerSite,
      getPageDeletes(s.siteId),
      getPostDeletes(s.siteId)
    )
  );
}
